import CheckedIcon from '@mui/icons-material/CheckCircle'
import { Timeline, TimelineConnector, TimelineContent, TimelineDot, TimelineItem, TimelineSeparator } from '@mui/lab'
import { Box, CardContent, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { isEmpty } from 'lodash'
import { useTranslate } from 'ra-core'
import { SimpleShowLayout } from 'react-admin'

import StaticText from './StaticText'
import { STATUS_CODES } from '../config/statuses'
import { THEME_MODE_LIGHT, useCommonStyles } from '../config/theme'
import { getStatusStyles } from '../domain/statuses'
import { CardTitle } from '../resources/common'
import { useShowStyles } from '../resources/common/show'
import { formatDateTime } from '../utils/dates'

const styles = {
  checkedIconContainer: {
    height: '28px',
    width: '28px',
    minWidth: '28px',
    borderRadius: '14px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 2,
    marginLeft: 1,
    marginRight: 2,
    marginBottom: 1,
    backgroundColor: 'success.main',
  },
  statusIcon: {
    color: 'white',
    width: 20,
    height: 20,
  },
  timelineContainer: {
    maxHeight: 328,
    overflow: 'auto',
    paddingBottom: 0,
    px: 2,
    mb: 2,
  },
  timelineDot: {
    width: 16,
    height: 16,
    boxShadow: 'none',
  },
  timelineDotOutline: {
    outline: 'dashed',
    outlineWidth: 2,
    outlineOffset: 3,
    outlineColor: (theme) => `grey.${theme.palette.mode === THEME_MODE_LIGHT ? '500' : '300'}`,
  },
  firstTimelineDot: {
    marginBottom: 1.5,
  },
  timelineContent: {
    paddingTop: '10px',
    paddingBottom: '20px',
    marginLeft: 1,
  },
  codesList: {
    marginTop: '6px',
    paddingLeft: '20px',
  },
  codesListItem: {
    fontSize: 14,
    marginBottom: '5px',
  },
}

const StatusTimelineDot = ({ status, index }) => {
  const theme = useTheme()
  const commonClasses = useCommonStyles()
  const { icon: StatusIcon, mainColor } = getStatusStyles(status, theme)

  return (
    <TimelineDot
      sx={{
        boxSizing: 'content-box',
        ...commonClasses.center,
        backgroundColor: mainColor,
        ...styles.timelineDot,
        ...(index === 0 ? { ...styles.firstTimelineDot, ...styles.timelineDotOutline } : {}),
      }}
    >
      <StatusIcon sx={styles.statusIcon} />
    </TimelineDot>
  )
}

const StatusTimeline = ({ data }) => {
  const translate = useTranslate()
  return (
    <Timeline sx={{ m: 0 }}>
      {data.map((status, index) => {
        const { timestamp, codes } = status
        const isLastItem = index + 1 === data.length
        return (
          <TimelineItem key={index}>
            <TimelineSeparator>
              <StatusTimelineDot status={status} index={index} />
              {!isLastItem && <TimelineConnector />}
            </TimelineSeparator>
            <TimelineContent sx={styles.timelineContent}>
              <Typography variant="subtitle2">{formatDateTime(timestamp)}</Typography>
              {!isEmpty(codes) && (
                <ul style={styles.codesList}>
                  {codes.map((code) => (
                    <li key={code} style={styles.codesListItem}>
                      {translate(STATUS_CODES[code])}
                    </li>
                  ))}
                </ul>
              )}
            </TimelineContent>
          </TimelineItem>
        )
      })}
    </Timeline>
  )
}

const OperationalStatusSection = ({ data }) => {
  const commonClasses = useCommonStyles()
  const showClasses = useShowStyles()

  return (
    <>
      <CardContent sx={{ ...showClasses.subTitleContainer, ...commonClasses.borderTop }}>
        <CardTitle text="mymove.operationalStatus.name" size={17} />
      </CardContent>
      <SimpleShowLayout
        sx={{
          '& .RaSimpleShowLayout-row': { margin: 0, width: '100%' },
          ...styles.timelineContainer,
          ...commonClasses.borderTop,
        }}
      >
        {Boolean(data) ? (
          isEmpty(data) ? (
            <div style={commonClasses.flexRowCenterContainer}>
              <Box
                sx={{
                  ...styles.checkedIconContainer,
                  ...styles.timelineDotOutline,
                }}
              >
                <CheckedIcon sx={styles.statusIcon} />
              </Box>
              <StaticText>mymove.operationalStatus.emptyMessage</StaticText>
            </div>
          ) : (
            <StatusTimeline data={data} />
          )
        ) : (
          <StaticText>mymove.operationalStatus.missingData</StaticText>
        )}
      </SimpleShowLayout>
    </>
  )
}

export default OperationalStatusSection
